// Compute per-body-segment pixel counts from a MediaPipe pose + segmentation mask.
//
// MediaPipe Pose Landmarker returns 33 landmarks; we use anatomical indices to
// define head / torso / arms / legs regions. For each mask pixel that's "person",
// we classify it into the segment whose region it falls into.
//
// This replaces the current server-side bounding-box-zone heuristic and works
// correctly for any riding position (upright, hoods, drops, TT, aero bars).

import { Landmark } from './poseDetector';

// Persistent scratch buffer for mask erosion (avoids per-frame allocation).
let _erodedMask: Uint8Array | null = null;

// MediaPipe Pose landmark indices we care about.
// Full list: https://developers.google.com/mediapipe/solutions/vision/pose_landmarker#pose_landmarker_model
const LM = {
  NOSE: 0,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
};

/** Per-pixel label values for the optional labelMap output. */
export const LABEL = {
  NONE: 0,
  HEAD: 1,
  TORSO: 2,
  ARMS: 3,
  LEGS: 4,
} as const;

export interface BodySegmentCounts {
  head: number;
  torso: number;
  arms: number;
  legs: number;
  /** Sum of classified segment pixels. May be slightly less than totalMask
   *  because some mask pixels fall outside the classifier's regions. */
  total: number;
  /** Total number of "person" pixels in the mask — use this for frontal area
   *  calculations, as it includes fringe pixels the classifier may have missed. */
  totalMask: number;
  bbox: [number, number, number, number]; // [x, y, w, h] in pixel coords
}

/**
 * Classify each "person" pixel in the mask into one of four body segments.
 * Returns per-segment pixel counts plus the bounding box of the person.
 *
 * Strategy:
 *  - HEAD: pixels above a line from shoulder-to-shoulder, roughly.
 *    We use the nose y-coordinate minus a head-height estimate.
 *  - TORSO: pixels inside the quadrilateral shoulder-shoulder-hip-hip.
 *  - ARMS: pixels within a band around each arm chain (shoulder->elbow->wrist),
 *    excluding the torso quadrilateral.
 *  - LEGS: everything else below the hip line.
 */
export function computeBodySegments(
  landmarks: Landmark[],
  mask: ImageData,
  /** Optional persistent Uint8Array (W*H) to fill with per-pixel LABEL values.
   *  Passing the same buffer each frame avoids allocation. */
  labelMap?: Uint8Array
): BodySegmentCounts | null {
  if (!landmarks || landmarks.length < 33) {
    return null;
  }

  const W = mask.width;
  const H = mask.height;
  const data = mask.data;

  // --- Mask erosion (1px) ---
  // MediaPipe's segmentation mask has fuzzy edges that extend 2-5 pixels
  // beyond the actual body boundary. A 1-pixel morphological erosion removes
  // the outermost layer, which is almost entirely segmentation noise/
  // feathering. A pixel survives only if ALL 4 of its neighbors are also
  // "person" in the original mask. We write into a scratch buffer so the
  // original mask is not modified (other consumers still need it).
  if (!_erodedMask || _erodedMask.length !== W * H) {
    _erodedMask = new Uint8Array(W * H);
  }
  const eroded = _erodedMask;
  const THRESH = 128;
  for (let y = 1; y < H - 1; y++) {
    const row = y * W;
    for (let x = 1; x < W - 1; x++) {
      const p = row + x;
      // Only keep pixel if center + all 4 neighbors are person
      eroded[p] =
        data[p * 4] >= THRESH &&
        data[(p - 1) * 4] >= THRESH &&
        data[(p + 1) * 4] >= THRESH &&
        data[(p - W) * 4] >= THRESH &&
        data[(p + W) * 4] >= THRESH
          ? 1
          : 0;
    }
  }
  // Border pixels are always eroded away (safe default)

  if (labelMap) {
    // Clear previous frame. Uint8Array.fill is very fast.
    labelMap.fill(LABEL.NONE);
  }

  // Convert normalized landmarks to pixel coords
  const px = (i: number) => landmarks[i].x * W;
  const py = (i: number) => landmarks[i].y * H;

  const shoulderMidY = (py(LM.LEFT_SHOULDER) + py(LM.RIGHT_SHOULDER)) / 2;
  const hipMidY = (py(LM.LEFT_HIP) + py(LM.RIGHT_HIP)) / 2;

  // Head boundary: half the shoulder-to-hip distance above the shoulder line.
  // This is an approximation; on a cyclist the head is typically one "torso unit"
  // or less above the shoulder line depending on neck extension.
  const torsoHeight = Math.abs(hipMidY - shoulderMidY);
  const headBottomY = shoulderMidY;
  const headTopY = shoulderMidY - torsoHeight * 0.7;

  // Torso quadrilateral (axis-aligned approximation: use left/right extremes of
  // shoulders and hips as the x bounds, and shoulder/hip y as y bounds).
  const torsoLeftX = Math.min(
    px(LM.LEFT_SHOULDER),
    px(LM.RIGHT_SHOULDER),
    px(LM.LEFT_HIP),
    px(LM.RIGHT_HIP)
  );
  const torsoRightX = Math.max(
    px(LM.LEFT_SHOULDER),
    px(LM.RIGHT_SHOULDER),
    px(LM.LEFT_HIP),
    px(LM.RIGHT_HIP)
  );

  // Arm segments as line segments with a band thickness. We'll check each
  // mask pixel against the distance to these line segments.
  const armChains: Array<[number, number, number, number]> = [
    // [x1, y1, x2, y2] pairs for each arm: shoulder->elbow, elbow->wrist
    [px(LM.LEFT_SHOULDER), py(LM.LEFT_SHOULDER), px(LM.LEFT_ELBOW), py(LM.LEFT_ELBOW)],
    [px(LM.LEFT_ELBOW), py(LM.LEFT_ELBOW), px(LM.LEFT_WRIST), py(LM.LEFT_WRIST)],
    [px(LM.RIGHT_SHOULDER), py(LM.RIGHT_SHOULDER), px(LM.RIGHT_ELBOW), py(LM.RIGHT_ELBOW)],
    [px(LM.RIGHT_ELBOW), py(LM.RIGHT_ELBOW), px(LM.RIGHT_WRIST), py(LM.RIGHT_WRIST)],
  ];
  const armBandRadius = torsoHeight * 0.15; // scale with body size

  let head = 0;
  let torso = 0;
  let arms = 0;
  let legs = 0;
  let totalMask = 0;
  let minX = W,
    minY = H,
    maxX = 0,
    maxY = 0;

  // Iterate mask pixels. mask.data is RGBA; we only need the R channel.
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const pIdx = y * W + x;
      // Use the eroded mask (not the raw mask) to skip edge noise
      if (!eroded[pIdx]) continue;

      totalMask++;

      // Update bbox
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;

      let label: number;

      // Head
      if (y >= headTopY && y < headBottomY) {
        head++;
        label = LABEL.HEAD;
      } else if (y < hipMidY) {
        // Upper body band (between shoulders and hips)
        if (x >= torsoLeftX && x <= torsoRightX) {
          torso++;
          label = LABEL.TORSO;
        } else if (isNearAnyArmSegment(x, y, armChains, armBandRadius)) {
          arms++;
          label = LABEL.ARMS;
        } else {
          // Fringe outside torso quadrilateral — most likely arm extending sideways
          arms++;
          label = LABEL.ARMS;
        }
      } else {
        // Below hips → legs
        legs++;
        label = LABEL.LEGS;
      }

      if (labelMap) labelMap[pIdx] = label;
    }
  }

  const total = head + torso + arms + legs;
  if (totalMask === 0) return null;

  return {
    head,
    torso,
    arms,
    legs,
    total,
    totalMask,
    bbox: [minX, minY, maxX - minX, maxY - minY],
  };
}

function isNearAnyArmSegment(
  px: number,
  py: number,
  segs: Array<[number, number, number, number]>,
  radius: number
): boolean {
  for (const [x1, y1, x2, y2] of segs) {
    if (distToSegment(px, py, x1, y1, x2, y2) <= radius) return true;
  }
  return false;
}

function distToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(px - x1, py - y1);
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.hypot(px - projX, py - projY);
}
